import React from "react";
import { Button, ButtonGroup, FormControl, FormGroup, FormLabel, InputGroup, Modal, Tab, Tabs } from "react-bootstrap";
import { DesignerElement } from "../../helpers";
import { DesignerFont } from "./DesignerFont";
import { ImageEditor } from "../";
import Slider from "rc-slider";
import 'rc-slider/assets/index.css';
import { DesignerAnimations } from "./DesignerAnimations";


interface Props {
  selectedElement?: DesignerElement,
  selectedIndex?: number,
  updateValue: (el: DesignerElement) => void,
  updateIndex: (newIndex: number) => void,
  remove: () => void
  duplicate: () => void
}

export const DesignerSidebar: React.FC<Props> = props => {
  const [showFont, setShowFont] = React.useState(false);
  const [showImageEditor, setShowImageEditor] = React.useState(false);





  const handleChange = (e: any) => {
    updateValue(e.currentTarget.id, e.currentTarget.value)
  }

  const toggleValue = (id: string) => {
    let el: DesignerElement = { ...props.selectedElement };
    switch (id) {
      case "bold": el.bold = !el.bold; break;
      case "italic": el.italic = !el.italic; break;
      case "underline": el.underline = !el.underline; break;
      case "textShadow": el.textShadow = !el.textShadow; break;
    }
    props.updateValue(el);
  }

  const updateValue = (id: string, val: string) => {
    let el: DesignerElement = { ...props.selectedElement };
    switch (id) {
      case "opacity": el.opacity = parseFloat(val); break;
      case "size": el.size = parseFloat(val); break;
      case "lineHeight": el.lineHeight = parseFloat(val); break;
      case "url": el.url = val; break;
      case "text": el.text = val; break;
      case "color": el.color = val; break;
      case "font": el.font = val; break;
      case "borderType":
        if (!el.border) el.border = { type: "solid", color: "#000000", width: 1 };
        if (!el.border.color) el.border.color = "#000000";
        if (!el.border.width) el.border.width = 1;
        el.border.type = val;
        break;
      case "borderColor":
        if (!el.border) el.border = { type: "solid", color: "#000000", width: 1 };
        el.border.color = val;
        break;
      case "borderWidth":
        if (!el.border) el.border = { type: "solid", color: "#000000", width: 1 };
        el.border.width = parseFloat(val);
        break;
      case "align":
        switch (val) {
          case "left": el.align = "left"; break;
          case "right": el.align = "right"; break;
          case "center": el.align = "center"; break;
        }
        break;
    }
    props.updateValue(el);
  }

  const propLabel = (label: string, value?: React.ReactNode) => (
    <FormLabel className="property-label">
      <span>{label}</span>
      {value !== undefined && <span className="property-value">{value}</span>}
    </FormLabel>
  );

  const colorRow = (id: string, label: string, value: string, fallback: string) => (
    <FormGroup className="property-row">
      {propLabel(label)}
      <div className="property-color">
        <FormControl id={id} type="color" value={value || fallback} onChange={handleChange} className="property-color-swatch" />
        <span className="property-color-hex">{(value || fallback).toUpperCase()}</span>
      </div>
    </FormGroup>
  );

  const getProperties = () => {
    if (props.selectedElement) {
      let styleLabel = "Style";
      let styleContent: React.ReactElement = <></>;
      switch (props.selectedElement.type) {
        case "text": styleLabel = "Text"; styleContent = getTextOptions(); break;
        case "image": styleLabel = "Image"; styleContent = getImageOptions(); break;
        case "box": styleLabel = "Box"; styleContent = getBoxOptions(); break;
      }
      return (<>
        <Tabs defaultActiveKey="style" id="designerSidebarTabs" className="designer-tabs" mountOnEnter unmountOnExit>
          <Tab eventKey="style" title={styleLabel}>{styleContent}</Tab>
          <Tab eventKey="layer" title="Layer">{getLayerOptions()}</Tab>
          <Tab eventKey="animations" title="Animations">
            <DesignerAnimations selectedElement={props.selectedElement} updateValue={props.updateValue} />
          </Tab>
        </Tabs>
        <div className="property-actions">
          <Button variant="outline-light" size="sm" onClick={e => { e.preventDefault(); props.duplicate() }}>
            <i className="fas fa-clone" aria-hidden="true"></i> Duplicate
          </Button>
          <Button variant="outline-danger" size="sm" onClick={e => { e.preventDefault(); props.remove() }}>
            <i className="fas fa-trash-alt" aria-hidden="true"></i> Remove
          </Button>
        </div>
      </>);
    }
  }


  const getBoxOptions = () => {
    const el = props.selectedElement;
    const opacity = el?.opacity ?? 100;
    const borderWidth = el?.border?.width ?? 1;
    return (<>
      {colorRow("color", "Fill", el.color, "#000099")}
      <FormGroup className="property-row">
        {propLabel("Opacity", `${Math.round(opacity)}%`)}
        <Slider value={opacity} min={1} max={100} step={1} onChange={(val) => { updateValue("opacity", val.toString()) }} />
      </FormGroup>

      <div className="property-section">Border</div>
      <FormGroup className="property-row">
        {propLabel("Style")}
        <FormControl id="borderType" as="select" value={el?.border?.type || ""} onChange={handleChange} size="sm" >
          <option value="">None</option>
          <option value="solid">Solid</option>
          <option value="dotted">Dotted</option>
          <option value="dashed">Dashed</option>
        </FormControl>
      </FormGroup>
      {colorRow("borderColor", "Color", el.border?.color, "#000000")}
      <FormGroup className="property-row">
        {propLabel("Width", borderWidth.toFixed(1))}
        <Slider value={borderWidth} min={0.1} max={3} step={0.1} onChange={(val) => { updateValue("borderWidth", val.toString()) }} />
      </FormGroup>
    </>);
  }

  const getTextOptions = () => {
    const el = props.selectedElement;
    const size = el?.size ?? 9;
    const lineHeight = el?.lineHeight ?? 1.5;
    return (<>
      <FormGroup className="property-row">
        {propLabel("Font")}
        <Button variant="dark" size="sm" className="property-button w-100" onClick={(e) => { e.preventDefault(); setShowFont(true); }}>
          <span style={{ fontFamily: el.font || "Roboto" }}>{el.font || "Roboto"}</span>
          <i className="fas fa-chevron-down ms-2" aria-hidden="true"></i>
        </Button>
      </FormGroup>
      <FormGroup className="property-row">
        {propLabel("Size", size.toFixed(1))}
        <Slider value={size} min={1} max={50} step={0.1} onChange={(val) => { updateValue("size", val.toString()) }} />
      </FormGroup>
      <FormGroup className="property-row">
        {propLabel("Line Height", lineHeight.toFixed(2))}
        <Slider value={lineHeight} min={0.5} max={2.5} step={0.1} onChange={(val) => { updateValue("lineHeight", val.toString()) }} />
      </FormGroup>
      <FormGroup className="property-row">
        {propLabel("Align")}
        <ButtonGroup className="property-segmented">
          <Button variant={(el.align === "left" || !el.align) ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); updateValue("align", "left") }} aria-label="Align left"><i className="fas fa-align-left" aria-hidden="true"></i></Button>
          <Button variant={(el.align === "center") ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); updateValue("align", "center") }} aria-label="Align center"><i className="fas fa-align-center" aria-hidden="true"></i></Button>
          <Button variant={(el.align === "right") ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); updateValue("align", "right") }} aria-label="Align right"><i className="fas fa-align-right" aria-hidden="true"></i></Button>
        </ButtonGroup>
      </FormGroup>
      <FormGroup className="property-row">
        {propLabel("Style")}
        <ButtonGroup className="property-segmented">
          <Button variant={el.bold ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); toggleValue("bold") }} aria-label="Bold" aria-pressed={el.bold}><i className="fas fa-bold" aria-hidden="true"></i></Button>
          <Button variant={el.italic ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); toggleValue("italic") }} aria-label="Italic" aria-pressed={el.italic}><i className="fas fa-italic" aria-hidden="true"></i></Button>
          <Button variant={el.underline ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); toggleValue("underline") }} aria-label="Underline" aria-pressed={el.underline}><i className="fas fa-underline" aria-hidden="true"></i></Button>
          <Button variant={el.textShadow ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); toggleValue("textShadow") }} aria-label="Text shadow" aria-pressed={el.textShadow}><i className="fas fa-clone" aria-hidden="true"></i></Button>
        </ButtonGroup>
      </FormGroup>
      {colorRow("color", "Color", el.color, "#FFFFFF")}
      <FormGroup className="property-row">
        {propLabel("Content")}
        <FormControl as="textarea" id="text" value={el?.text || ""} onChange={handleChange} size="sm" rows={3} placeholder="Enter text..." />
      </FormGroup>
    </>);
  }


  const handleImageUpdate = (dataUrl: string) => {
    if (dataUrl) updateValue("url", dataUrl);
    setShowImageEditor(false);
  }

  const getImageEditor = () => {
    if (!showImageEditor) return null;
    return (
      <Modal show={true} onHide={() => setShowImageEditor(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImageEditor startingUrl={props.selectedElement.url} updatedFunction={handleImageUpdate} name="imageEditor" aspectRatio={0} height={200} />
        </Modal.Body>
      </Modal>
    );
  }

  const setAspectRatio = (val: boolean | number) => {
    const el: DesignerElement = { ...props.selectedElement, lockAspectRatio: val };
    props.updateValue(el);
  }

  const getImageOptions = () => {
    const lock = props.selectedElement?.lockAspectRatio;
    const isFree = lock === false || lock === undefined;
    const aspectBtn = (val: boolean | number, label: string, title?: string) => (
      <Button variant={lock === val ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); setAspectRatio(val); }} title={title}>{label}</Button>
    );
    return (<>
      <FormGroup className="property-row">
        {propLabel("Aspect Ratio")}
        <div className="property-aspect-grid">
          <Button variant={isFree ? "secondary" : "outline-light"} size="sm" onClick={(e) => { e.preventDefault(); setAspectRatio(false); }}>Free</Button>
          {aspectBtn(true, "Lock", "Lock current ratio")}
          {aspectBtn(16 / 9, "16:9")}
          {aspectBtn(9 / 16, "9:16")}
          {aspectBtn(4 / 3, "4:3")}
          {aspectBtn(1, "1:1")}
        </div>
      </FormGroup>
      <FormGroup className="property-row">
        {propLabel("Image")}
        <Button variant="primary" size="sm" className="property-button w-100" onClick={(e) => { e.preventDefault(); setShowImageEditor(true); }}>
          <i className="fas fa-image me-2" aria-hidden="true"></i> Select Image
        </Button>
      </FormGroup>
      {getImageEditor()}
    </>);
  }

  const getLayerOptions = () => {
    return (
      <FormGroup className="property-row">
        {propLabel("Order", `Layer ${(props.selectedIndex ?? 0) + 1}`)}
        <InputGroup size="sm">
          <Button variant="outline-light" onClick={(e) => { e.preventDefault(); props.updateIndex(props.selectedIndex - 1); }} aria-label="Send backward">
            <i className="fas fa-arrow-down" aria-hidden="true"></i> Back
          </Button>
          <Button variant="outline-light" onClick={(e) => { e.preventDefault(); props.updateIndex(props.selectedIndex + 1); }} aria-label="Bring forward">
            Front <i className="fas fa-arrow-up" aria-hidden="true"></i>
          </Button>
        </InputGroup>
      </FormGroup>
    );
  }

  const getFont = () => {
    if (showFont) return <DesignerFont onClose={() => { setShowFont(false) }} updateValue={(val) => { setShowFont(false); updateValue("font", val) }} />
  }


  return (
    <>
      {getFont()}
      <div id="designerSidebar">
        {getProperties()}

      </div>
    </>
  );

}